import { getSectionHTML } from '../utils/section-renderer';
import type ProductCarousel from './product-carousel';

const STORAGE_KEY = 'recentlyViewedProductHandles';
const MAX_PRODUCTS = 8;

export default class RecentlyViewedProducts extends HTMLElement {
  private grid: HTMLElement | null = null;
  private productCardWrapperTemplate: HTMLTemplateElement | null = null;

  connectedCallback(): void {
    this.grid = this.querySelector('[data-product-carousel-ref="grid"]');
    this.productCardWrapperTemplate = this.querySelector(
      '[data-recently-viewed-products-template="product-card-wrapper"]',
    );

    if (!this.grid || !this.currentProductHandle) {
      this.classList.add('hidden');
      return;
    }

    this.writeCurrentProductHandle();
    this.renderRecentlyViewedProducts();
  }

  private get currentProductHandle(): string | null {
    const handle = this.dataset.productHandle?.trim();
    return handle ? handle : null;
  }

  private get productCardSectionId(): string {
    return this.dataset.productCardSectionId || 'product-card-ajax';
  }

  private readHandles(): string[] {
    try {
      const rawValue = window.localStorage.getItem(STORAGE_KEY);
      if (!rawValue) {
        return [];
      }

      const parsedValue: unknown = JSON.parse(rawValue);
      if (!Array.isArray(parsedValue)) {
        return [];
      }

      return parsedValue.filter((item): item is string => typeof item === 'string' && !!item.trim());
    } catch (error) {
      console.error(error);
      return [];
    }
  }

  private writeHandles(handles: string[]): void {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
    } catch (error) {
      console.error(error);
    }
  }

  private writeCurrentProductHandle(): void {
    if (!this.currentProductHandle) {
      return;
    }

    const existingHandles = this.readHandles().filter((handle) => handle !== this.currentProductHandle);
    const nextHandles = [...existingHandles.slice(-MAX_PRODUCTS + 1), this.currentProductHandle];
    this.writeHandles(nextHandles);
  }

  private async renderRecentlyViewedProducts(): Promise<void> {
    if (!this.grid || !this.currentProductHandle) {
      this.classList.add('hidden');
      return;
    }

    const handlesToRender = this.readHandles()
      .filter((handle) => handle !== this.currentProductHandle)
      .slice(-MAX_PRODUCTS)
      .reverse();

    if (!handlesToRender.length) {
      this.classList.add('hidden');
      return;
    }

    const cards: string[] = [];

    for (const handle of handlesToRender) {
      try {
        const cardHtml = await this.fetchProductCard(handle);
        if (cardHtml) {
          cards.push(cardHtml);
        }
      } catch (error) {
        console.error(error);
      }
    }

    if (!cards.length) {
      this.classList.add('hidden');
      return;
    }

    this.grid.innerHTML = cards.join('');
    this.classList.remove('hidden');
    this.querySelector<ProductCarousel>('product-carousel')?.refresh();
  }

  private async fetchProductCard(handle: string): Promise<string | null> {
    // @ts-expect-error Shopify is injected on the storefront
    const productUrl = new URL(`${window.Shopify.routes.root}products/${handle}`, window.location.origin);
    const sectionHtml = await getSectionHTML(this.productCardSectionId, productUrl, false);
    const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const card = parsed.querySelector('product-card');

    if (card && this.productCardWrapperTemplate) {
      const outputFragment = this.productCardWrapperTemplate.content.cloneNode(true) as DocumentFragment;
      const output = outputFragment.firstElementChild;

      if (!output) {
        return null;
      }

      output.appendChild(card);
      return output.outerHTML.trim();
    }

    return null;
  }
}

customElements.define('recently-viewed-products', RecentlyViewedProducts);
